/**
 * Role Service
 * 
 * Factory for role data providers. Selects between Mock and Gobodo
 * based on environment configuration.
 * 
 * Part of PE-894: Create roleService.ts with Gobodo CRUD
 * 
 * Usage:
 *   import { roleService } from './roleService';
 *   const roles = await roleService.getRoles();
 */

import { RoleProvider } from './providers/roleProvider';
import { MockRoleProvider, mockRoleProvider } from './providers/mockRoleProvider';
import { GobodoRoleProvider, gobodoRoleProvider } from './providers/gobodoRoleProvider';

// Re-export types for convenience
export type {
  Role,
  RoleProvider,
  RoleFilters,
  RoleHierarchyNode,
  CreateRoleRequest,
  UpdateRoleRequest,
  ObjectPermission,
  SearchPermissions,
  PermissionLevel,
} from './providers/roleProvider';

/**
 * Check if Gobodo API should be used
 */
function shouldUseGobodo(): boolean {
  return process.env.REACT_APP_USE_GOBODO_API === 'true';
}

/**
 * Get the appropriate role provider based on configuration
 * 
 * When REACT_APP_USE_GOBODO_API=true:
 *   - Uses GobodoRoleProvider (calls Gobodo API)
 *   - Falls back to MockRoleProvider if Gobodo fails
 * 
 * When REACT_APP_USE_GOBODO_API=false or unset:
 *   - Uses MockRoleProvider (localStorage)
 */
export function getRoleProvider(): RoleProvider {
  if (shouldUseGobodo()) {
    return gobodoRoleProvider;
  }
  return mockRoleProvider;
}

/**
 * Create a role provider with specific options
 * Useful for testing or specific use cases
 */
export function createRoleProvider(options: {
  type: 'mock' | 'gobodo';
  simulateDelay?: boolean;
}): RoleProvider {
  if (options.type === 'gobodo') {
    return new GobodoRoleProvider();
  }
  return new MockRoleProvider({ simulateDelay: options.simulateDelay });
}

/**
 * Role Service with Fallback
 * 
 * Wraps the provider to handle Gobodo failures gracefully.
 * When Gobodo fails, automatically falls back to mock data.
 */
class RoleServiceWithFallback implements RoleProvider {
  private primary: RoleProvider;
  private fallback: RoleProvider;
  private useFallback: boolean = false;

  constructor() {
    this.primary = shouldUseGobodo() ? gobodoRoleProvider : mockRoleProvider;
    this.fallback = mockRoleProvider;
  }

  private async withFallback<T>(
    operation: () => Promise<T>,
    fallbackOperation: () => Promise<T>,
    operationName: string
  ): Promise<T> {
    // If already using fallback, use it directly
    if (this.useFallback) {
      return fallbackOperation();
    }

    try {
      const result = await operation();
      // Check if result is empty array (Gobodo has no data)
      if (Array.isArray(result) && result.length === 0 && shouldUseGobodo()) {
        console.log(`[RoleService] ${operationName}: Gobodo returned empty, using fallback`);
        return fallbackOperation();
      }
      return result;
    } catch (error) {
      console.warn(`[RoleService] ${operationName} failed, falling back to mock:`, error);
      this.useFallback = true;
      return fallbackOperation();
    }
  }

  async getRoles(filters?: Parameters<RoleProvider['getRoles']>[0]) {
    return this.withFallback(
      () => this.primary.getRoles(filters),
      () => this.fallback.getRoles(filters),
      'getRoles'
    );
  }

  async getRoleById(id: string) {
    return this.withFallback(
      () => this.primary.getRoleById(id),
      () => this.fallback.getRoleById(id),
      'getRoleById'
    );
  }

  async createRole(request: Parameters<RoleProvider['createRole']>[0]) {
    return this.withFallback(
      () => this.primary.createRole(request),
      () => this.fallback.createRole(request),
      'createRole'
    );
  }

  async updateRole(id: string, updates: Parameters<RoleProvider['updateRole']>[1]) {
    return this.withFallback(
      () => this.primary.updateRole(id, updates),
      () => this.fallback.updateRole(id, updates),
      'updateRole'
    );
  }

  async deleteRole(id: string) {
    return this.withFallback(
      () => this.primary.deleteRole(id),
      () => this.fallback.deleteRole(id),
      'deleteRole'
    );
  }

  async cloneRole(id: string, newName: string) {
    return this.withFallback(
      () => this.primary.cloneRole(id, newName),
      () => this.fallback.cloneRole(id, newName),
      'cloneRole'
    );
  }

  async getRoleHierarchy() {
    return this.withFallback(
      () => this.primary.getRoleHierarchy(),
      () => this.fallback.getRoleHierarchy(),
      'getRoleHierarchy'
    );
  }

  /**
   * Reset the service to use primary provider again
   * Call this after fixing Gobodo connection issues
   */
  resetFallback(): void {
    this.useFallback = false;
  }

  /**
   * Check if currently using fallback
   */
  isUsingFallback(): boolean {
    return this.useFallback;
  }
}

// Export singleton service instance with fallback support
export const roleService = new RoleServiceWithFallback();

// Export class for testing
export { RoleServiceWithFallback };
